<template>
  <Teleport to="body">
    <Transition name="offcanvas">
      <div v-if="isOpen" class="offcanvas-overlay" @click="$emit('close')">
        <div class="offcanvas-menu" @click.stop>
          <div class="offcanvas-header">
            <div class="offcanvas-header-logo" @click="$emit('close')">
              <LayoutHeaderLogo />
            </div>
            <button class="close-btn" @click="$emit('close')">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          
          <!-- Profile header — shown when logged in and dev mode active -->
          <div v-if="isLoggedIn && devMode" class="offcanvas-profile">
            <div class="offcanvas-profile__avatar">{{ initials }}</div>
            <div class="offcanvas-profile__info">
              <span class="offcanvas-profile__name">{{ displayName }}</span>
              <span v-if="profile?.contact_email" class="offcanvas-profile__email">{{ profile.contact_email }}</span>
            </div>
          </div>

          <nav class="mobile-nav">
            <template v-for="item in menuItems" :key="item.key">
              <NuxtLink
                v-if="!item.scrollTo"
                :to="item.href"
                class="mobile-nav-link"
                :class="{ 'mobile-nav-link-active': isItemActive(item) }"
                @click="$emit('close')"
              >
                {{ item.label }}
              </NuxtLink>
              <NuxtLink
                v-else
                :to="{ path: `/${currentLocale}`, hash: `#${item.scrollTo}` }"
                class="mobile-nav-link"
                :class="{ 'mobile-nav-link-active': isItemActive(item) }"
                @click="goToSection(item.scrollTo!)"
              >
                {{ item.label }}
              </NuxtLink>
            </template>

            <!-- Auth nav items — gated by devMode -->
            <template v-if="devMode && isLoggedIn">
              <NuxtLink class="mobile-nav-link" to="/my/progress" @click="$emit('close')">{{ t('layout.header.student.progress', 'Мій прогрес') }}</NuxtLink>
              <!-- profile link hidden until /my/profile is ready for prod -->
              <!-- <NuxtLink class="mobile-nav-link" to="/my/profile" @click="$emit('close')">{{ t('layout.header.student.profile', 'Профіль') }}</NuxtLink> -->
              <button class="mobile-nav-link mobile-nav-link-button mobile-nav-link--danger" @click="handleLogout">{{ t('layout.header.student.logout', 'Вийти') }}</button>
            </template>
            <button v-else-if="devMode && authEnabled" class="mobile-nav-link mobile-nav-link-button" @click="handleLogin">{{ t('layout.header.actions.loginButton', 'Увійти') }}</button>
          </nav>

          <div class="mobile-offcanvas-meta">
            <!-- <ContactInfo class="mobile-offcanvas-contact-info" /> -->
            <LazyLayoutHeaderLangSelector
              class="mobile-offcanvas-lang"
              :current-locale="currentLocale" 
              :fallback-locale="fallbackLocale"
              @locale-change="$emit('localeChange', $event)"
            />
            <UIButton v-if="supportUrl" :href="supportUrl" variant="primary" padding="4px 12px" target="_blank" class="mobile-offcanvas-support-btn" @click="$emit('close')">
              {{ t('layout.header.actions.supportButton') }}
            </UIButton>
            <div class="mobile-offcanvas-social">
              <h3 class="mobile-offcanvas-social-title">
                {{ t('layout.footer.main.socialNetworksTitle') }}
              </h3>
              <div class="mobile-offcanvas-social-links">
                <a
                  v-for="item in mobileSocialLinks"
                  :key="item.name"
                  :href="item.url"
                  class="mobile-offcanvas-social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click="$emit('close')"
                >
                  <img
                    :src="item.image"
                    :alt="item.name"
                    width="40"
                    height="40"
                    loading="lazy"
                    decoding="async"
                  >
                </a>
              </div>
            </div>
          </div>

          <div class="mobile-actions"></div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { watch, onBeforeUnmount, nextTick } from 'vue'
import { useStudentStore } from '@/stores/student'
import { useStudentLoginModal } from '@/composables/study/useStudentLoginModal'
import { useDevMode } from '@/composables/app/useDevMode'

const config = useAppConfig()
const mobileSocialLinks = computed(() => (config.site?.socialLinks ?? []) as { name: string; url: string; image: string }[])
const supportUrl = computed(() => config.site?.supportUrl as string | null ?? null)

const studentStore = useStudentStore()
const { isLoggedIn, displayName, initials, profile } = storeToRefs(studentStore)
const authEnabled = useRuntimeConfig().public.AUTH_ENABLED as boolean
const { openLoginModal } = useStudentLoginModal()
const { devMode } = useDevMode()

function handleLogin() {
  emit('close')
  openLoginModal()
}

async function handleLogout() {
  emit('close')
  await studentStore.logout()
  router.push('/')
}

const props = defineProps<{
  isOpen: boolean
  currentLocale?: string
  fallbackLocale?: string
}>()

const emit = defineEmits<{
  close: []
  localeChange: [locale: string]
}>()

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()

const currentLocale = computed(() => (route.params.locale ?? locale.value) as string)

// Flagship behaviour: "About" is a dedicated page. Locale sites override this
// whole component via the landing2-locale layer to scroll instead.
type MenuItem = { key: string; label: string; href?: string; scrollTo?: string }

const menuItems = computed(() => {
  const items: MenuItem[] = [
    { key: 'home', label: t('layout.header.navigation.home'), href: `/${currentLocale.value}` },
    { key: 'courses', label: t('layout.header.navigation.courses'), href: `/${currentLocale.value}`, scrollTo: 'courses-section' },
    { key: 'about', label: t('layout.header.navigation.about'), href: `/${currentLocale.value}/about` },
    // { key: 'blog', label: t('layout.header.navigation.blog'), href: `/${currentLocale.value}/articles` },
    // { key: 'events', label: t('layout.header.navigation.events'), href: `/${currentLocale.value}/events` },
  ]
  items.push({ key: 'contacts', label: t('layout.header.navigation.contacts'), href: `/${currentLocale.value}/contacts` })
  return items
})

function isItemActive (item: MenuItem) {
  const homePath = `/${currentLocale.value}`
  if (item.scrollTo) {
    return route.path === homePath && route.hash === `#${item.scrollTo}`
  }
  if (item.href === homePath) {
    return route.path === homePath && !route.hash
  }
  return route.path === item.href || (item.href !== homePath && route.path.startsWith(item.href ?? ''))
}

function goToSection (sectionId: string) {
  emit('close')
  const homePath = `/${currentLocale.value}`
  if (route.path === homePath) {
    nextTick(() => {
      const el = document.getElementById(sectionId)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
        router.replace({ path: homePath, hash: `#${sectionId}` })
      } else {
        router.push({ path: homePath, hash: `#${sectionId}` })
      }
    })
  } else {
    router.push({ path: homePath, hash: `#${sectionId}` })
  }
}

// Блокування прокрутки body коли відкрите меню
watch(() => props.isOpen, (isOpen) => {
  if (process.client) {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
  }
})

// Очищення при unmount компонента
onBeforeUnmount(() => {
  if (process.client) {
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''
  }
})
</script>

<style scoped>
.offcanvas-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: flex-end;
  overflow: hidden;
}

.offcanvas-menu {
  background: #fff;
  width: 480px;
  max-width: 100%;
  height: 100vh;
  max-height: 100vh;
  padding: 20px;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.offcanvas-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.offcanvas-header-logo {
  display: flex;
}

.offcanvas-header-logo :deep(.logo-text) {
  height: 28px;
  width: auto;
  min-width: 0;
}

.close-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: #00CE9B;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #00CE9B;
}

.offcanvas-profile {
  display: flex; align-items: center; gap: 12px;
  padding: 16px 0 20px; border-bottom: 1px solid rgba(0,0,0,0.06); margin-bottom: 8px;
}
.offcanvas-profile__avatar {
  width: 44px; height: 44px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, var(--gradient-primary-start, #09AFEA), var(--gradient-primary-end, #19E089));
  color: #fff; font-size: 16px; font-weight: 300; font-family: var(--font-inter);
  display: flex; align-items: center; justify-content: center; letter-spacing: 0.5px;
}
.offcanvas-profile__info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.offcanvas-profile__name { font-size: 15px; font-weight: 600; color: var(--color-dark-blue); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.offcanvas-profile__email { font-size: 12px; color: var(--color-grey); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.mobile-nav {
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
}

.mobile-nav-link {
  font-family: var(--font-inter);
  font-weight: 500;
  font-size: 18px;
  color: #252840;
  text-decoration: none;
  padding: 12px 0;
  transition: color 0.2s ease;
}

.mobile-nav-link:hover,
.mobile-nav-link.mobile-nav-link-active {
  color: #00CE9B;
}

.mobile-nav-link-button {
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
}

.mobile-nav-link--danger { color: var(--color-error, #e53e3e) !important; }
.mobile-nav-link--danger:hover { color: var(--color-error, #e53e3e) !important; }

.mobile-offcanvas-meta {
  display: flex;
  flex-direction: column;
  gap: 28px;
  margin-bottom: 28px;
}

.mobile-offcanvas-contact-info :deep(.contact-info-title) {
  font-size: 18px;
}

.mobile-offcanvas-social-title {
  font-family: var(--font-inter);
  font-weight: 600;
  font-size: 18px;
  line-height: 1.2;
  color: var(--color-dark-blue, #252840);
  margin: 0 0 16px;
}

.mobile-offcanvas-social-links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.mobile-offcanvas-social-link {
  display: flex;
  line-height: 0;
  border-radius: 8px;
  overflow: hidden;
  transition: opacity 0.2s ease;
}

.mobile-offcanvas-social-link:hover {
  opacity: 0.85;
}

.mobile-offcanvas-social-link img {
  display: block;
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.mobile-actions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: auto;
}

.mobile-offcanvas-lang :deep(.lang-selector) {
  width: 72px;
}

.mobile-offcanvas-lang :deep(.lang-btn) {
  width: auto;
  justify-content: space-between;
  padding: 10px 10px;
  background: transparent;
  border: 1px solid rgba(37, 40, 64, 0.14);
  border-radius: 8px;
}

.mobile-actions :deep(.btn) {
  width: 100%;
  justify-content: center;
}

.mobile-offcanvas-support-btn {
  align-self: flex-start;
}

.mobile-offcanvas-support-btn :deep(.btn) {
  width: auto;
}

.mobile-offcanvas-student-menu {
  align-self: flex-start;
}

/* In offcanvas context: dropdown opens downward with enough space */
.mobile-offcanvas-student-menu :deep(.student-menu) {
  position: static;
  box-shadow: none;
  border: 1px solid rgba(0,0,0,0.06);
  margin-top: 8px;
}

/* Transitions */
.offcanvas-enter-active,
.offcanvas-leave-active {
  transition: opacity 0.3s ease;
}

.offcanvas-enter-active .offcanvas-menu,
.offcanvas-leave-active .offcanvas-menu {
  transition: transform 0.3s ease;
}

.offcanvas-enter-from,
.offcanvas-leave-to {
  opacity: 0;
}

.offcanvas-enter-from .offcanvas-menu,
.offcanvas-leave-to .offcanvas-menu {
  transform: translateX(100%);
}
</style>
